import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useI18n } from '../../contexts/I18nContext';
import { useSubscriptionAccess } from '../../contexts/SubscriptionAccessContext';
import { useBrand } from '../../../brands/contexts/BrandContext';
import {
  buildSubscriptionPaymentFailUrl,
  buildSubscriptionPaymentSuccessUrlBase,
  getActiveSubscriptionOrNull,
  resetSubscriptionRebill,
  startSubscriptionPayment,
  type SubscriptionDetailsResponse,
} from '../../utils/subscriptionApiUtils';
import { protocolLimitLabel, tariffPriceLabel } from './subscriptionFormatters';
import './SubscriptionPages.scss';

const SubscriptionPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useI18n();
  const { brand } = useBrand();
  const { mustPurchaseSubscription } = useSubscriptionAccess();
  const [data, setData] = useState<SubscriptionDetailsResponse | null | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const logoUrl = `${process.env.PUBLIC_URL}/${brand.logos.small}`;
  const origin = useMemo(() => window.location.origin, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const sub = await getActiveSubscriptionOrNull();
      setData(sub);
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : t('subscription.loadError'), { variant: 'error' });
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const sub = data?.subscription;
  const tariff = sub?.tariff;
  const hasSubscription = !!sub;

  useEffect(() => {
    if (!loading && !hasSubscription) {
      navigate('/subscription/tariffs', { replace: true });
    }
  }, [loading, hasSubscription, navigate]);

  const handleStartResetLimitsPayment = async () => {
    const tariffId = sub?.tariff_id ?? sub?.tariff?.id;
    if (!tariffId || !sub?.billing_period) {
      enqueueSnackbar(t('subscription.resetLimitsPaymentError'), { variant: 'error' });
      return;
    }
    setActionLoading(true);
    try {
      const successUrl = buildSubscriptionPaymentSuccessUrlBase(origin);
      const failUrl = buildSubscriptionPaymentFailUrl(origin);
      sessionStorage.setItem('subscriptionPostPaymentRedirect', '/subscription');
      sessionStorage.setItem('subscriptionPostPaymentSnackbar', 'subscription.resetLimitsOk');
      const { payment_url } = await startSubscriptionPayment({
        tariff_id: tariffId,
        billing_period: sub.billing_period,
        success_url: successUrl,
        fail_url: failUrl,
        reset: true,
      });
      window.location.href = payment_url;
    } catch (e) {
      sessionStorage.removeItem('subscriptionPostPaymentRedirect');
      sessionStorage.removeItem('subscriptionPostPaymentSnackbar');
      enqueueSnackbar(e instanceof Error ? e.message : t('subscription.resetLimitsPaymentError'), {
        variant: 'error',
      });
      setActionLoading(false);
    }
  };

  const handleCancelRebill = async () => {
    if (!window.confirm(t('subscription.cancelConfirm'))) return;
    setActionLoading(true);
    try {
      await resetSubscriptionRebill();
      await load();
      enqueueSnackbar(t('subscription.cancelOk'), { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : t('subscription.error'), { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="subscription-page">
        <div className="subscription-page__loader">{t('subscription.loading')}</div>
      </div>
    );
  }

  if (!hasSubscription) {
    return (
      <div className="subscription-page">
        <div className="subscription-page__loader">{t('subscription.loading')}</div>
      </div>
    );
  }

  return (
    <div className="subscription-page">
      <header
        className={`subscription-page__header${mustPurchaseSubscription ? ' subscription-page__header--solo' : ''}`}
      >
        <h1 className="subscription-page__title">{t('subscription.title')}</h1>
      </header>

      <div className="subscription-dashboard">
        <div className="subscription-hero-card">
          <div className="subscription-hero-card__main">
            <div className="subscription-hero-card__badge">
              <img src={logoUrl} alt="" className="subscription-hero-card__logo" />
            </div>
            <div className="subscription-hero-card__text">
              <div className="subscription-hero-card__name">{tariff?.name ?? '—'}</div>
              <div className="subscription-hero-card__sub">{t('subscription.currentPlan')}</div>
              {tariff && (
                <p className="subscription-hint">{protocolLimitLabel(tariff)}</p>
              )}
            </div>
          </div>
          {tariff && (
            <div className="subscription-hero-card__price-wrap">
              <div className="subscription-hero-card__price">
                {tariffPriceLabel(tariff, sub.billing_period)}
              </div>
            </div>
          )}
        </div>

        <div className="subscription-stats-row">
          <div className="subscription-stat-card">
            <div className="subscription-stat-card__label">{t('subscription.nextPayment')}</div>
            <div className="subscription-stat-card__value">
              {sub.next_billing_date
                ? new Date(sub.next_billing_date).toLocaleDateString('ru-RU')
                : '—'}
            </div>
          </div>
          <div className="subscription-stat-card">
            <div className="subscription-stat-card__label">{t('subscription.limitLeft')}</div>
            <div className="subscription-stat-card__value">
              {sub.protocols_balance} {t('subscription.of')} {sub.protocol_limit_total}
            </div>
          </div>
          <div className="subscription-stat-card">
            <div className="subscription-stat-card__label">{t('subscription.autoRenew')}</div>
            <div className="subscription-stat-card__value subscription-stat-card__value--inline">
              <span
                className={`subscription-auto-badge${sub.auto_renew ? ' subscription-auto-badge--on' : ' subscription-auto-badge--off'}`}
              >
                {sub.auto_renew ? t('subscription.autoRenewOn') : t('subscription.autoRenewOff')}
              </span>
            </div>
          </div>
        </div>

        <div className="subscription-actions-grid">
          <section className="subscription-section-card">
            <h2 className="subscription-section-card__title">{t('subscription.resetLimitTitle')}</h2>
            <p className="subscription-section-card__desc">{t('subscription.resetLimitDesc')}</p>
            <button
              type="button"
              className="subscription-btn subscription-btn--primary subscription-btn--block"
              disabled={actionLoading}
              onClick={() => void handleStartResetLimitsPayment()}
            >
              {t('subscription.resetLimitBtn')}
            </button>
          </section>

          <section className="subscription-section-card">
            <h2 className="subscription-section-card__title">{t('subscription.changeTariffTitle')}</h2>
            <p className="subscription-section-card__desc">{t('subscription.changeTariffDesc')}</p>
            <button
              type="button"
              className="subscription-btn subscription-btn--primary subscription-btn--block"
              onClick={() => navigate('/subscription/tariffs')}
            >
              {t('subscription.changeTariffBtn')}
            </button>
          </section>
        </div>

        {sub.auto_renew !== false && (
          <section className="subscription-cancel-card" aria-labelledby="subscription-cancel-heading">
            <div className="subscription-cancel-card__icon" aria-hidden>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <path
                  d="m15 9-6 6M9 9l6 6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <div className="subscription-cancel-card__body">
              <h2 id="subscription-cancel-heading" className="subscription-cancel-card__title">
                {t('subscription.cancelSectionTitle')}
              </h2>
              <p className="subscription-cancel-card__desc">{t('subscription.cancelSectionDesc')}</p>
              <button
                type="button"
                className="subscription-btn subscription-btn--danger-outline subscription-btn--block"
                disabled={actionLoading}
                onClick={() => void handleCancelRebill()}
              >
                {t('subscription.cancelSubscription')}
              </button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPage;
